import { useEffect, useRef, useState } from 'react';
import { taxPaymentSettingsGateway } from '../index';
import type { StateTaxHistoryItem } from '../types';
import { formatDate, formatDateTime } from '../taxPaymentView';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

interface AuditHistoryModalProps {
  jurisdictionKey: string;
  jurisdiction: string;
  subtitle: string;
  onClose: () => void;
}

/** Read-only change history, loaded from the server's append-only record (every entry added, every future entry withdrawn). Never built from screen state. */
export function AuditHistoryModal({ jurisdictionKey, jurisdiction, subtitle, onClose }: AuditHistoryModalProps) {
  const [items, setItems] = useState<StateTaxHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    taxPaymentSettingsGateway
      .getStateTaxHistory(jurisdictionKey, controller.signal)
      .then(setItems)
      .catch((err) => { if (!controller.signal.aborted) setError(friendlyErrorMessage(err, 'Could not load the audit history.')); });
    return () => controller.abort();
  }, [jurisdictionKey]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="tps-scrim tps-scrim--modal" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tps-modal" role="dialog" aria-modal="true" aria-labelledby="tps-audit-title">
        <button type="button" className="tps-close" aria-label="Close audit history" onClick={onClose}><span className="tps-ico tps-ico--x" aria-hidden="true" /></button>
        <h3 id="tps-audit-title">Audit History - {jurisdiction}</h3>
        <p className="tps-muted">{subtitle}All tax program changes</p>

        {error && <p className="tps-error" role="alert">{error}</p>}
        {!items && !error && <p role="status">Loading…</p>}
        {items && items.length === 0 && <p className="tps-muted">No changes have been recorded yet.</p>}
        {items && items.length > 0 && (
          <div className="tps-modal__scroll">
            <table className="tps-table" aria-label={`${jurisdiction} audit history`}>
              <thead>
                <tr>
                  <th scope="col">Tax Type</th>
                  <th scope="col">Rate / Schedule</th>
                  <th scope="col">Effective Date</th>
                  <th scope="col">Change</th>
                  <th scope="col">Updated On</th>
                  <th scope="col">Updated By</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={`${i.entryId}-${i.action}`}>
                    <td><span className="tps-chip">{i.componentAbbreviation}</span></td>
                    <td>{i.value}</td>
                    <td>{formatDate(i.effectiveFrom)}</td>
                    <td>{i.action}</td>
                    <td>{formatDateTime(i.occurredAtUtc)}</td>
                    <td>{i.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="tps-modal__footer">
          <button ref={closeRef} type="button" className="tps-btn tps-btn--primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
